import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Mail, Calendar, TrendingUp, CheckCircle, XCircle } from 'lucide-react';
import { fetchAdminUsers } from '../../services/adminApi';

const PageContainer = styled.div`
  width: 100%;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, var(--primary-light) 0%, rgba(255, 143, 0, 0.05) 100%);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 143, 0, 0.2);
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid var(--gray-200);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: var(--gray-100);
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid var(--gray-200);

  &:hover {
    background-color: var(--gray-50);
  }
`;

const Td = styled.td`
  padding: 1rem;
  font-size: 0.9375rem;
  color: var(--gray-700);
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--gray-500);
`;

const Badge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => props.$sent ? 'var(--success-light)' : 'var(--warning-light)'};
  color: ${props => props.$sent ? 'var(--success-color)' : 'var(--warning-color)'};
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.125rem;
  color: var(--gray-500);
`;

const ErrorText = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.125rem;
  color: var(--danger-color);
`;

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAdminUsers();
      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <LoadingText>Loading users...</LoadingText>;
  }

  if (error) {
    return <ErrorText>Error loading users: {error}</ErrorText>;
  }

  const discountSentCount = users.filter(u => u.discount_code_sent).length;
  const discountUsedCount = users.filter(u => u.discount_code_used).length;

  return (
    <PageContainer>
      <StatsContainer>
        <StatCard>
          <StatValue>{users.length}</StatValue>
          <StatLabel>Total Users</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{discountSentCount}</StatValue>
          <StatLabel>Discounts Sent</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{discountUsedCount}</StatValue>
          <StatLabel>Discounts Used</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{users.reduce((sum, u) => sum + (u.access_count || 0), 0)}</StatValue>
          <StatLabel>Total Access Count</StatLabel>
        </StatCard>
      </StatsContainer>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Email</Th>
              <Th>Access Count</Th>
              <Th>Discount Status</Th>
              <Th>Created At</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr key={user.id}>
                <Td>
                  <IconWrapper>
                    <Mail size={16} />
                    {user.email}
                  </IconWrapper>
                </Td>
                <Td>
                  <IconWrapper>
                    <TrendingUp size={16} />
                    {user.access_count || 0}
                  </IconWrapper>
                </Td>
                <Td>
                  {user.discount_code_used ? (
                    <Badge $sent={true}>
                      <CheckCircle size={12} />
                      Used
                    </Badge>
                  ) : user.discount_code_sent ? (
                    <Badge $sent={true}>
                      Sent
                    </Badge>
                  ) : (
                    <Badge $sent={false}>
                      <XCircle size={12} />
                      Not Sent
                    </Badge>
                  )}
                </Td>
                <Td>
                  <IconWrapper>
                    <Calendar size={16} />
                    {formatDate(user.created_at)}
                  </IconWrapper>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default UsersPage;
